import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { Button, Field, Stack, Text } from '@/design-system'
import { useAuth } from '@/app/auth/auth-context'
import { AuthFooter, AuthHeading, OrDivider, SocialRow } from './AuthForm'
import { BRAND } from '@/brand/brand'

export function LoginScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  const [email, setEmail] = useState(`alex@${BRAND.domain}`)
  const [password, setPassword] = useState('white1')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const destination = (location.state as { from?: string } | null)?.from ?? '/'

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (!email.includes('@')) {
      setError('Enter the email you opened your account with.')
      return
    }
    if (password.length < 6) {
      setError('Your password is at least six characters.')
      return
    }

    setSubmitting(true)
    try {
      await login({ email, password })
      navigate(destination, { replace: true })
    } catch {
      setError('We could not sign you in. Try again in a moment.')
      setSubmitting(false)
    }
  }

  return (
    <Stack as="form" gap={6} onSubmit={handleSubmit}>
      <AuthHeading title="Log in" subtitle="Welcome back. Enter your details to continue." />

      <Stack gap={3}>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          {...(error && !email.includes('@') ? { error } : {})}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          {...(error && password.length < 6 ? { error } : {})}
        />
      </Stack>

      {error && email.includes('@') && password.length >= 6 && (
        <Text variant="caption" tone="negative" role="alert">
          {error}
        </Text>
      )}

      <Stack direction="row" justify="end">
        <Link to="/forgot-password">
          <Text variant="caption" weight="semibold">
            Forgot your password?
          </Text>
        </Link>
      </Stack>

      <Button type="submit" size="lg" block disabled={submitting}>
        {submitting ? 'Logging in…' : 'Log in'}
      </Button>

      <OrDivider />
      <SocialRow />

      <AuthFooter question={`New to ${BRAND.name}?`} actionLabel="Create an account" to="/onboarding" />
    </Stack>
  )
}
